import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ResourceType } from '@cotera/api';
import type { Conversation } from '@cotera/sdk/core';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import type { CocoChatModel } from '../models/coco-chat.model';
import type { ResolvedChatSeedContext } from '../../chat-seed/resolve-seed-context';
import {
  getChatSeedStrategy,
  parseChatSeedFolderId,
  resourceTypeForChatSeedType,
} from '../../chat-seed';

type FolderScope = 'user' | 'org' | 'external';

type ResourceMeta = {
  folderId?: string | null;
  folderScope?: FolderScope | null;
};

export type ChatBreadcrumbFolderResolutionInput = {
  conversation: Conversation | null;
  modelFolderId: string | undefined;
  seedContext: ResolvedChatSeedContext | null;
  urlFolderId: string | undefined;
  stateFolderId: string | undefined;
  seedArtifactFolderId: string | null | undefined;
  seedArtifactFolderScope: FolderScope | null | undefined;
  seedArtifactFolderLoading: boolean;
  isNewChat: boolean;
};

export type ChatBreadcrumbFolderResolution = {
  folderId: string | null | undefined;
  folderScope?: FolderScope | null;
};

export function resolveChatBreadcrumbFolder(
  input: ChatBreadcrumbFolderResolutionInput
): ChatBreadcrumbFolderResolution {
  if (input.conversation !== null) {
    const conversationFolderId = input.conversation.getFolderId();
    if (conversationFolderId !== null) {
      return { folderId: conversationFolderId };
    }
  }

  if (input.modelFolderId !== undefined) {
    return { folderId: input.modelFolderId };
  }

  const fromNavigation =
    input.seedContext?.input.folderId ??
    input.urlFolderId ??
    input.stateFolderId;
  if (fromNavigation !== undefined) {
    return { folderId: fromNavigation };
  }

  if (input.seedArtifactFolderLoading) {
    return { folderId: undefined };
  }

  if (input.seedArtifactFolderId !== undefined) {
    return {
      folderId: input.seedArtifactFolderId,
      folderScope: input.seedArtifactFolderScope ?? undefined,
    };
  }

  if (input.isNewChat) {
    return { folderId: null };
  }

  return { folderId: undefined };
}

type UseChatBreadcrumbFolderParams = {
  conversation: Conversation | null;
  cocoModel: CocoChatModel;
  searchParams: URLSearchParams;
  seedContext: ResolvedChatSeedContext | null;
  isNewChat: boolean;
};

export function useChatBreadcrumbFolder({
  conversation,
  cocoModel,
  searchParams,
  seedContext,
  isNewChat,
}: UseChatBreadcrumbFolderParams): ChatBreadcrumbFolderResolution {
  const client = useTenantedClient();
  const org = useWhoami((s) => s.org);
  const location = useLocation();
  const stateFolderId = (location.state as { folderId?: string } | null)
    ?.folderId;
  const urlFolderId = parseChatSeedFolderId(searchParams);

  const seedResourceType =
    seedContext !== null
      ? getChatSeedStrategy(seedContext.type)?.artifactResourceType ??
        resourceTypeForChatSeedType(seedContext.type)
      : undefined;

  const seedArtifactId =
    seedContext !== null && seedResourceType !== undefined
      ? seedContext.input.artifactId
      : undefined;

  const hasKnownFolderFromNavigation =
    (conversation !== null && conversation.getFolderId() !== null) ||
    cocoModel.folderId !== undefined ||
    seedContext?.input.folderId !== undefined ||
    urlFolderId !== undefined ||
    stateFolderId !== undefined;

  // New seeded agents/workflows are not on the server yet — probing them writes a
  // NotFound into the shared `resource.get` cache, which ChatArtifactAccessGate
  // then treats as "unshared" until a full page refresh.
  const seedArtifactIsNew = seedContext?.input.isNew === true;

  const { data: seedArtifactResource, isLoading: seedArtifactFolderLoading } =
    useQuery({
      queryKey: queryKeys.resource.get({
        orgId: org.id,
        type: seedResourceType ?? ResourceType.Chat,
        id: seedArtifactId ?? '',
      }),
      queryFn: () =>
        client.resource.get({
          type: seedResourceType!,
          id: seedArtifactId!,
        }),
      enabled:
        seedResourceType !== undefined &&
        seedArtifactId !== undefined &&
        !hasKnownFolderFromNavigation &&
        !seedArtifactIsNew,
      staleTime: 60_000,
    });

  const seedArtifactFolderId = useMemo((): string | null | undefined => {
    if (seedArtifactResource === undefined) {
      return undefined;
    }
    if (seedArtifactResource.isErr()) {
      return null;
    }
    const meta = (seedArtifactResource.value as { meta?: ResourceMeta | null })
      .meta;
    return meta?.folderId ?? null;
  }, [seedArtifactResource]);

  const seedArtifactFolderScope = useMemo(():
    | FolderScope
    | null
    | undefined => {
    if (seedArtifactResource === undefined || seedArtifactResource.isErr()) {
      return undefined;
    }
    const meta = (seedArtifactResource.value as { meta?: ResourceMeta | null })
      .meta;
    return meta?.folderScope ?? null;
  }, [seedArtifactResource]);

  return useMemo(
    () =>
      resolveChatBreadcrumbFolder({
        conversation,
        modelFolderId: cocoModel.folderId,
        seedContext,
        urlFolderId,
        stateFolderId,
        seedArtifactFolderId,
        seedArtifactFolderScope,
        seedArtifactFolderLoading:
          seedResourceType !== undefined &&
          seedArtifactId !== undefined &&
          !hasKnownFolderFromNavigation &&
          seedArtifactFolderLoading,
        isNewChat,
      }),
    [
      conversation,
      cocoModel.folderId,
      seedContext,
      urlFolderId,
      stateFolderId,
      seedArtifactFolderId,
      seedArtifactFolderScope,
      seedResourceType,
      seedArtifactId,
      hasKnownFolderFromNavigation,
      seedArtifactFolderLoading,
      isNewChat,
    ]
  );
}
